import { ArrowRightIcon, PlusIcon } from 'lucide-react'
import { useMemo, useState } from 'react'

import { Button, ButtonGroup, TextButton, TextLink, useBreakpoint } from 'slab'
import { snakeToSentenceCase } from 'utils'
import { MemberOnboardingMemberGender } from 'utils/web_api.schemas'

import { OnboardingClearEnrollmentsModal } from '../components/OnboardingClearEnrollmentsModal'
import RenewalsAddDependent from '../components/RenewalsAddDependent/RenewalsAddDependent'
import RenewalsCoveredDependent from '../components/RenewalsCoveredDependent/RenewalsCoveredDependent'
import RenewalsMemberInfo from '../components/RenewalsMemberInfo/RenewalsMemberInfo'
import { useOnboarding } from '../OnboardingContext'
import { type StepComponentProps } from '../types'

export function RenewalConfirmInfoPage ({
  navigateToNextStep,
}: StepComponentProps) {
  const {
    onboardingData,
    additionalParticipants,
    enrollmentEventId,
    refreshAdditionalParticipants,
    refreshOnboardingInfo,
  } = useOnboarding()
  const { breakpointIsAtLeast } = useBreakpoint()
  const [addDependentOpen, setAddDependentOpen] = useState(false)
  // Corner case to validate in case if the user does not have household income specified (for legacy data)
  const isMissingHouseholdIncome =
    onboardingData?.missing_fields?.plans_selection?.includes(
      'member.household_income'
    ) ?? false
  const [disableNext, setDisableNext] = useState(
    isMissingHouseholdIncome
  )
  const planYear = onboardingData?.enrollment_event?.plan_year

  // Construct gender options to be passed for add dependent
  const restrictions = onboardingData?.restrictions
  const binaryGenderRestriction =
    restrictions?.binary_gender_required?.length &&
    restrictions.binary_gender_required.length > 0

  const genderOptions = useMemo(
    () =>
      Object.values(MemberOnboardingMemberGender)
        .filter((gender) => {
          if (binaryGenderRestriction) {
            return gender !== MemberOnboardingMemberGender.non_binary
          }
          return true
        })
        .map((gender) => ({
          value: gender,
          label: snakeToSentenceCase(gender),
        })),
    [binaryGenderRestriction]
  )

  // Check if the user already has a spouse among existing participants
  const hasExistingSpouse =
    additionalParticipants?.data?.some(
      (participant) =>
        participant.relationship === 'spouse' &&
        participant.selected_plan_status !== 'will_be_removed'
    ) || false

  // Collect SSNs for the dependent for duplicate SSN validation
  const ssnsToValidate =
    additionalParticipants?.data
      ?.filter(
        (participant) => participant.selected_plan_status !== 'will_be_removed'
      )
      .map((participant) => participant.ssn?.replace(/\D/g, ''))
      .filter((ssn) => ssn !== undefined) || []
  if (onboardingData?.member?.ssn) {
    ssnsToValidate.push(onboardingData.member.ssn.replace(/\D/g, ''))
  }

  // Modal state for clearing enrollments
  const [showModal, setShowModal] = useState(false)
  const [modalSubmitted, setModalSubmitted] = useState(false)
  const [pendingAction, setPendingAction] = useState<(() => void) | null>(null)

  const onRefreshPage = async () => {
    await refreshAdditionalParticipants()
    await refreshOnboardingInfo()
  }

  const handleAddDependent = async () => {
    setAddDependentOpen(false)
    setDisableNext(false)
    await onRefreshPage()
  }

  // Modal handlers
  const onTriggerModal = (action?: () => void) => {
    if (action) {
      setPendingAction(() => action)
    }
    setShowModal(true)
  }

  const handleModalClose = () => {
    setShowModal(false)
    setPendingAction(null)
  }

  const handleModalSubmit = () => {
    setModalSubmitted(true)
    setShowModal(false)
    if (pendingAction) {
      pendingAction()
      setPendingAction(null)
    }
  }

  return (
    <div data-testid='RenewalConfirmInfoPage' className='max-w-[44rem]'>
      <div className='mb-10'>
        <h1 className='heading-lg text-gray-800 mb-3'>
          Make sure we have the right info
        </h1>
        <p className='body text-gray-500 mb-3'>
          Let us know who needs coverage next year, and double-check the details
          we have on file for everyone.
        </p>
        <p className='body text-gray-500'>
          If something besides your home address or cell phone number looks
          incorrect, you can reach out to{' '}
          <TextLink color='purple' href='mailto:support@thatch.com'>
            support@thatch.com
          </TextLink>{' '}
          to get it updated.
        </p>
      </div>
      <div className='mb-6'>
        <div className='mb-4'>
          <h2 className='heading-sm text-gray-800'>You</h2>
        </div>
        <div className='flex flex-col gap-y-2'>
          {onboardingData?.member && (
            <RenewalsMemberInfo
              member={onboardingData.member}
              handleEditOpen={() => {
                setDisableNext(true)
              }}
              handleEditSuccessful={async () => {
                await refreshOnboardingInfo()
                setDisableNext(false)
              }}
              handleEditCancel={() => {
                setDisableNext(false)
              }}
              isMissingHouseholdIncome={isMissingHouseholdIncome}
            />
          )}
        </div>
      </div>
      <div className='mb-10'>
        <div className='mb-4 flex flex-row items-center justify-between gap-x-2'>
          <h2 className='heading-sm text-gray-800'>Your dependents</h2>
          {!addDependentOpen && (
            <TextButton
              leftIcon={PlusIcon}
              color='purple'
              onClick={() => {
                setAddDependentOpen(true)
                setDisableNext(true)
              }}
            >
              Add dependent
            </TextButton>
          )}
        </div>
        <div className='flex flex-col gap-y-2'>
          {addDependentOpen && (
            <RenewalsAddDependent
              genderOptions={genderOptions}
              handleCancel={() => {
                setAddDependentOpen(false)
                setDisableNext(false)
              }}
              handlePostAction={handleAddDependent}
              hasExistingSpouse={hasExistingSpouse}
              onTriggerModal={onTriggerModal}
              modalSubmitted={modalSubmitted}
              modalClosed={!showModal}
              dependentSSNs={ssnsToValidate}
            />
          )}
          {!!additionalParticipants?.data?.length &&
            additionalParticipants.data.length > 0 &&
            additionalParticipants?.data.map((participant) => (
              <RenewalsCoveredDependent
                key={participant.id}
                coveredParticipant={participant}
                enrollmentEventId={enrollmentEventId}
                planYear={planYear}
                handleRefresh={onRefreshPage}
                onTriggerModal={onTriggerModal}
                setDisableNext={setDisableNext}
                modalSubmitted={modalSubmitted}
                modalClosed={!showModal}
                hasExistingSpouse={hasExistingSpouse}
              />
            ))}
        </div>
      </div>
      <ButtonGroup
        size='large'
        layout={breakpointIsAtLeast('xs') ? 'horizontal' : 'vertical'}
      >
        <Button
          rightIcon={ArrowRightIcon}
          variant='primary'
          type='button'
          disabled={disableNext}
          onClick={() => {
            navigateToNextStep()
          }}
        >
          Next
        </Button>
      </ButtonGroup>

      <OnboardingClearEnrollmentsModal
        isModalOpen={showModal}
        onClose={handleModalClose}
        onSubmit={handleModalSubmit}
        description='Dependent information can impact plan cost and availability, so making a change here will remove any plans currently in your cart'
      />
    </div>
  )
}
